import React, { useState, useMemo } from "react";
import { EDataTable } from "../../../components/EDataTable";
import { IconButton, TextButton } from "../../../components/ButtonComponents";
import { TableActions } from "../../../components/TableActions";
import { FileText, Trash2, PlusCircle, ArrowUpRight, ArrowDownLeft, Calendar, Edit3, ArrowDown, ArrowDown01, ArrowDownCircle, ArrowUpCircle } from "lucide-react";
import { toast } from "react-toastify";
import { NavLink } from "react-router-dom";
import { useAccountMovementStore } from "../../../store/accountMovementStore";
import { useConfirmModal } from "../../../hooks/useConfirmModal";
import { useApiErrorHandler } from "../../../hooks/useApiErrorHandler";
import { AccountMovement } from "../../../types/accountMovement";
import { DataTableColumn, GroupFilter } from "../../../types";
import { InvoiceDialog } from "./InvoiceDialog";
import { usePermission } from "../../../hooks/usePermission";
import { InvoiceViewDialog } from "./InvoiceViewDialog";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import { Send, FileCode, Download, RefreshCcw } from "lucide-react";
import { useSettingsStore } from "../../../store/settingsStore";
import { DateRangePicker } from "../../../components/DateRangePicker";

export const InvoiceList = () => {
    const store = useAccountMovementStore();
    const { deleteItem, createItem } = store;
    const { handleApiError } = useApiErrorHandler();
    const { confirm, modal: confirmModal } = useConfirmModal();
    const { canCreate, canUpdate, canDelete } = usePermission('invoiceModule');
    const { modules } = useSettingsStore();
    const hasEInvoice = modules?.eInvoiceModule;

    const [viewInvoice, setViewInvoice] = useState<AccountMovement | null>(null);
    const [isViewOpen, setIsViewOpen] = useState(false);
    const [editInvoice, setEditInvoice] = useState<any>(null);

    // E-Fatura HTML Önizleme State
    const [previewHtml, setPreviewHtml] = useState<string | null>(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    const [filterDates, setFilterDates] = useState<(Date | null)[] | null>(null);

    const invoiceFilter: GroupFilter = useMemo(() => ({
        logic: "or",
        filters: [
            { field: "type", operator: "eq", value: "INVOICE" },
        ]
    }), []);

    const combinedFilters = React.useMemo(() => {
        const filters: GroupFilter[] = [invoiceFilter];
        if (filterDates && Array.isArray(filterDates) && filterDates[0] && filterDates[1]) {
            const startDate = new Date(filterDates[0]);
            startDate.setHours(0, 0, 0, 0);
            const endDate = new Date(filterDates[1]);
            endDate.setHours(23, 59, 59, 999);
            filters.push({
                logic: "and",
                filters: [
                    { field: "date", operator: "gte", value: startDate.toISOString() },
                    { field: "date", operator: "lte", value: endDate.toISOString() }
                ]
            });
        }
        return filters;
    }, [invoiceFilter, filterDates]);

    const handleViewClick = (invoice: AccountMovement) => {
        setViewInvoice(invoice);
        setIsViewOpen(true);
    };

    const handleEditClick = (row: AccountMovement) => {
        // Backend'den gelen row'u direkt set ediyoruz
        setEditInvoice(row);

        const type = row.direction === 'OUT' ? 'SALES' : 'PURCHASE';
        setInvoiceType(type);

        setIsInvoiceOpen(true);
    };
    const [isInvoiceOpen, setIsInvoiceOpen] = useState(false);
    const [invoiceType, setInvoiceType] = useState<'PURCHASE' | 'SALES'>('SALES');



    // --- RENDER TEMPLATES ---

    // Tarih ve Vade Tarihi
    const dateBodyTemplate = (row: AccountMovement) => (
        <div className="flex flex-col gap-0.5 min-w-0">
            <div className="flex items-center gap-1.5 font-medium truncate w-full">
                <Calendar size={12} className="shrink-0" />
                <span className="truncate">{new Date(row.date).toLocaleDateString('tr-TR')}</span>
            </div>

        </div>
    );

    const typeBodyTemplate = (row: AccountMovement) => {
        const isSales = row.direction === 'OUT';
        return (
            <div className={`flex items-center gap-2 px-2.5 py-1 rounded-full w-fit max-w-full font-bold text-[10px] border truncate ${isSales
                ? "bg-emerald-50 text-emerald-700 border-emerald-100" // Satış (Yeşil)
                : "bg-blue-50 text-blue-700 border-blue-100"      // Alış (Mavi)
                }`}>
                <div className="shrink-0">{isSales ? <ArrowUpRight size={12} /> : <ArrowDownLeft size={12} />}</div>
                <span className="truncate">{isSales ? "SATIŞ FATURASI" : "ALIŞ FATURASI"}</span>
            </div>
        );
    };

    // Cari Hesap Detayı
    const accountBodyTemplate = (row: AccountMovement) => (
        <div className="flex flex-col min-w-0">
            <span className="font-bold uppercase tracking-tight truncate block w-full">{row.account?.name || 'Bilinmeyen Cari'}</span>
            <span className="text-[10px] font-mono truncate block w-full">ID: #{row.accountId}</span>
        </div>
    );

    // Tutar Bilgileri (Matrah + KDV + Toplam)
    const amountBodyTemplate = (row: AccountMovement) => {
        const normalizeCurrency = (code: string | null | undefined) => {
            if (!code) return 'TRY';
            return code === 'TL' ? 'TRY' : code;
        };
        const normalizedCurrency = normalizeCurrency(row.currency);
        const isForeign = normalizedCurrency !== 'TRY';
        const exchangeRate = row.exchangeRate || 1;

        return (
            <div className="flex flex-col items-end gap-1 min-w-0">
                {/* Ana Tutar (Faturanın kesildiği orijinal döviz) */}
                <span className="font-black text-sm truncate block max-w-full">
                    {new Intl.NumberFormat('tr-TR', {
                        style: 'currency',
                        currency: normalizedCurrency
                    }).format(row.amount)}
                </span>

                {/* Eğer dövizli ise TL karşılığını ve kuru göster */}
                {isForeign && (
                    <div className="flex flex-col items-end border-t border-slate-100 pt-1 w-full min-w-0">
                        <span className="text-[10px] text-slate-500 font-bold truncate block max-w-full">
                            {new Intl.NumberFormat('tr-TR', {
                                style: 'currency',
                                currency: 'TRY'
                            }).format(row.amount * exchangeRate)}
                        </span>
                        <span className="text-[9px] text-blue-500 italic font-medium">
                            Kur: {exchangeRate.toFixed(4)}
                        </span>
                    </div>
                )}

                {/* Matrah ve KDV Bilgileri (TL bazında veya döviz simgesiyle) */}
                <div className="flex gap-2 text-[9px] font-semibold uppercase text-gray-400 mt-1 min-w-0 w-full justify-end">
                    <span className="truncate">M: {new Intl.NumberFormat('tr-TR').format(row.baseAmount)}</span>
                    <span className="text-blue-400 truncate">K: {new Intl.NumberFormat('tr-TR').format(row.taxAmount)}</span>
                </div>
            </div>
        );
    };
    const referenceBodyTemplate = (row: AccountMovement) => (
        <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-1 min-w-0">
                <span className="font-mono font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-100 w-fit text-xs truncate max-w-full">
                    {row.number || 'NO-REF'}
                </span>
                {row.currency && row.currency !== 'TRY' && (
                    <span className="bg-amber-100 text-amber-700 text-[9px] px-1 rounded font-bold shrink-0">
                        {row.currency}
                    </span>
                )}
            </div>
            {row.description && <span className="text-[10px] truncate block w-full mt-1">{row.description}</span>}
        </div>
    );
    const statusBodyTemplate = (row: AccountMovement) => {
        const isEInvoice = row.eInvoiceProfile === "EARSIVFATURA" || row.eInvoiceProfile === "TEMELFATURA";

        return (
            <div className="flex flex-col gap-1 min-w-0">
                <div className={`px-2 py-0.5 rounded text-[10px] font-bold border w-fit truncate max-w-full ${isEInvoice
                    ? "bg-purple-50 text-purple-700 border-purple-100"
                    : "bg-gray-50 text-gray-600 border-gray-200"
                    }`}>
                    {isEInvoice ? "E-ARŞİV / E-FATURA" : "KAĞIT FATURA"}
                </div>
            </div>
        );
    };

    const eInvoiceStatusTemplate = (row: AccountMovement) => {
        if (!hasEInvoice || (row.eInvoiceProfile !== "EARSIVFATURA" && row.eInvoiceProfile !== "TEMELFATURA")) return null;

        let severity: "success" | "warning" | "danger" | "info" = "info";
        let statusText = row.eInvoiceStatus || "BEKLİYOR";

        if (statusText.includes("ONAY") || statusText === "1300") {
            severity = "success";
            statusText = "ONAYLANDI";
        } else if (statusText.includes("HATA") || statusText.includes("İPTAL")) {
            severity = "danger";
        } else if (statusText.includes("KUYRUK") || statusText.includes("GÖNDERİLİYOR")) {
            severity = "warning";
        }

        return (
            <div className="flex flex-col gap-1">
                <span className="text-[11px] font-bold">{row.eInvoiceNumber || '-'}</span>
                {row.eInvoiceStatus && <Tag value={statusText} severity={severity} className="text-[10px] px-2 py-0.5" />}
            </div>
        );
    };
    const invoiceColumns: DataTableColumn<AccountMovement>[] = [
        {
            field: "date",
            header: "Tarih",
            width: "140px",
            body: dateBodyTemplate,
            sortable: true
        },
        {
            field: "direction",
            header: "İşlem Türü",
            width: "170px",
            body: typeBodyTemplate,
            sortable: true
        },
        {
            field: "number",
            header: "Fatura No",
            width: "240px",
            body: referenceBodyTemplate,
            sortable: true
        },
        // 🔥 YENİ EKLENEN SÜTUN
        {
            field: "eInvoiceProfile",
            header: "Belge Türü",
            width: "160px",
            body: statusBodyTemplate,
            sortable: true
        },
        ...(hasEInvoice ? [{
            field: "eInvoiceStatus",
            header: "E-Fatura Durumu",
            width: "170px",
            body: eInvoiceStatusTemplate,
            sortable: true
        }] : []),
        {
            field: "account.name",
            header: "Cari Hesap",
            width: "200px",
            body: accountBodyTemplate,
            sortable: true
        },
        {
            field: "amount",
            header: "Genel Toplam",
            width: "200px",
            align: "right",
            body: amountBodyTemplate,
            sortable: true
        },
        {
            field: "actions",
            header: "İşlemler",
            width: "150px",
            align: "center",
            body: (row) => {
                const isEInvoice = row.eInvoiceProfile === "EARSIVFATURA" || row.eInvoiceProfile === "TEMELFATURA";
                const isSent = !!row.eInvoiceUuid;
                const hasError = row.eInvoiceStatus?.includes("HATA");

                return (
                    <TableActions
                        actions={[
                            {
                                icon: Send,
                                colorClass: "text-purple-600",
                                title: "GİB'e Gönder",
                                onClick: () => handleSendEInvoice(row),
                                show: hasEInvoice && isEInvoice && !isSent
                            },
                            {
                                icon: FileCode,
                                colorClass: "text-indigo-500",
                                title: "HTML Önizle",
                                onClick: () => handlePreviewHtml(row),
                                show: hasEInvoice && isSent
                            },
                            {
                                icon: Download,
                                colorClass: "text-emerald-500",
                                title: "PDF İndir",
                                onClick: () => handleDownloadPdf(row),
                                show: hasEInvoice && isSent
                            },
                            {
                                icon: RefreshCcw,
                                colorClass: "text-amber-500",
                                title: "Yeniden Gönder",
                                onClick: () => handleSendEInvoice(row),
                                show: hasEInvoice && isSent && hasError
                            },
                            {
                                icon: FileText,
                                colorClass: "text-blue-500",
                                title: "Görüntüle",
                                onClick: () => handleViewClick(row)
                            },
                            {
                                icon: Edit3,
                                colorClass: "text-orange-400",
                                title: "Düzenle",
                                onClick: () => handleEditClick(row)
                            },
                            {
                                icon: Trash2,
                                colorClass: "text-red-400",
                                title: "Sil",
                                onClick: () => handleDeleteClick(row)
                            }
                        ]}
                    />
                );
            }
        },
    ];

    const handleSendEInvoice = async (row: AccountMovement) => {
        try {
            await store.sendToGib(row.id);
            toast.success("Fatura GİB kuyruğuna eklendi.");
        } catch (err) {
            handleApiError(err);
        }
    };

    const handlePreviewHtml = async (row: AccountMovement) => {
        if (!row.eInvoiceUuid) return;
        try {
            const htmlStr = await store.getInvoiceHtml(row.eInvoiceUuid, 'OUTBOUND', row.direction || 'OUT');
            setPreviewHtml(htmlStr);
            setIsPreviewOpen(true);
        } catch (err) {
            handleApiError(err);
        }
    };

    const handleDownloadPdf = async (row: AccountMovement) => {
        if (!row.eInvoiceUuid) return;
        try {
            await store.getInvoicePdf(row.eInvoiceUuid, 'OUTBOUND');
            toast.success("PDF indiriliyor...");
        } catch (err) {
            handleApiError(err);
        }
    };

    const handleDeleteClick = async (invoice: AccountMovement) => {
        const ok = await confirm(`${invoice.reference || 'Fatura'} nolu belgeyi silmek istediğinize emin misiniz? Bu işlem stokları da etkileyecektir.`);
        if (ok) {
            try {
                await deleteItem(invoice.id);
                toast.success("Fatura başarıyla iptal edildi.");
            } catch (err) { handleApiError(err); }
        }
    };

    const handleOpenInvoice = (type: 'PURCHASE' | 'SALES') => {
        setEditInvoice(null); // 🔥 Önceki düzenleme verisini temizle
        setInvoiceType(type);
        setIsInvoiceOpen(true);
    };

    const handleSaveInvoice = async (payload: any) => {
        try {
            if (payload.id) {
                const invoiceId = payload.id;
                const { id, ...dataWithoutId } = payload;
                await store.updateItem(invoiceId, dataWithoutId);
                toast.success("Fatura başarıyla güncellendi.");
            } else {
                await store.createItem(payload);
                toast.success("Fatura başarıyla kaydedildi.");
            }
            setIsInvoiceOpen(false);
            setEditInvoice(null); // Kayıttan sonra state'i temizle
        } catch (error) {
            handleApiError(error);
        }
    };

    return (
        <div className="h-full">
            {confirmModal}
            <EDataTable<AccountMovement>
                tableKey="invoice_list"
                useStore={() => store}
                dataKey="id"
                title="Fatura Yönetimi"
                placeholder="Fatura No, Açıklama veya Cari Ara..."
                filters={combinedFilters}
                filterBar={
                    <div className="w-full xl:w-auto flex-shrink-0 mb-2">
                        <DateRangePicker value={filterDates as any} onChange={setFilterDates as any} />
                    </div>
                }
                searchFields={[
                    { field: "number", operator: "contains" }, // Fatura numarasını ekledik
                    { field: "account.name", operator: "contains" },
                    { field: "description", operator: "contains" }
                ]}
                actions={
                    <div className="flex gap-3">
                        <TextButton onClick={() => handleOpenInvoice('SALES')} className="bg-green-600 text-white hover:bg-green-700 px-4 py-2 rounded-lg flex items-center gap-2">
                            <ArrowUpCircle />Yeni Satış
                        </TextButton>
                        <TextButton onClick={() => handleOpenInvoice('PURCHASE')} className="bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-lg flex items-center gap-2" >
                            <ArrowDownCircle /> Yeni Alış
                        </TextButton>

                    </div>
                }
                columns={invoiceColumns}
            />
            <InvoiceViewDialog
                invoice={viewInvoice}
                isOpen={isViewOpen}
                onClose={() => setIsViewOpen(false)}
            />
            <InvoiceDialog
                isOpen={isInvoiceOpen}
                onClose={() => {
                    setIsInvoiceOpen(false);
                    setEditInvoice(null); // Kapatırken temizle
                }}
                type={invoiceType}
                initialData={editInvoice} // Veriyi buraya paslıyoruz
                onSave={handleSaveInvoice}
            />

            {/* HTML Önizleme Dialogu */}
            <Dialog
                header="E-Fatura Görseli"
                visible={isPreviewOpen}
                style={{ width: '80vw', height: '90vh' }}
                onHide={() => {
                    setIsPreviewOpen(false);
                    setPreviewHtml(null);
                }}
                maximizable
            >
                {previewHtml ? (
                    <iframe
                        srcDoc={previewHtml}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title="E-Fatura Önizleme"
                    />
                ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                        Önizleme yükleniyor...
                    </div>
                )}
            </Dialog>
        </div>
    );
};